/**
 * Phase 6: `layout` events (docs/phase6/layout-containers.md).
 *
 * A `layout` element is a custom SwiftUI `Layout` evaluated on the Swift side
 * with host-measured subviews. After every layout pass the engine leaves, per
 * `layout` element, the proposal it was placed with and its children's min /
 * ideal / max sizes (`LayoutResult.layouts`). `LayoutReporter` turns those
 * into `layout` events and sends one only when the record differs from the
 * last one sent for that id, so Swift's answer (an `update` of the element's
 * `size` and `frames`) can trigger another pass without looping forever.
 *
 * DOM-free so it can be unit-tested next to the engine.
 */

import type { LayoutRecord, LayoutResult, Size } from './layout';
import type { Dimension, ElementId, LayoutChildMeasurement, LayoutEvent, LayoutProposal } from './protocol';

/** Round to 2 decimals; `Infinity` (an unbounded axis) serializes as `"infinity"`. */
export function layoutDimension(value: number): Dimension {
  if (!Number.isFinite(value)) return value > 0 ? 'infinity' : 0;
  return Math.round(value * 100) / 100;
}

function layoutSize(size: Size): { width: Dimension; height: Dimension } {
  return { width: layoutDimension(size.width), height: layoutDimension(size.height) };
}

/** The `layout` event for an engine record. */
export function layoutEventFor(id: ElementId, record: LayoutRecord): LayoutEvent {
  const axis = (value: number | null): LayoutProposal['width'] => (value === null ? null : layoutDimension(value));
  const children: LayoutChildMeasurement[] = record.children.map((child) => ({
    min: layoutSize(child.min),
    ideal: layoutSize(child.ideal),
    max: layoutSize(child.max),
  }));
  return {
    type: 'layout',
    id,
    proposal: { width: axis(record.proposal.width), height: axis(record.proposal.height) },
    children,
  };
}

/** How long (ms) the cycle guard remembers a record sent for an element. */
export const CYCLE_WINDOW_MS = 1000;

interface SentRecord {
  json: string;
  at: number;
}

export class LayoutReporter {
  /** The last event sent for every `layout` element. */
  readonly reported = new Map<ElementId, LayoutEvent>();
  /** The records sent per element within the cycle window (newest last). */
  private readonly history = new Map<ElementId, SentRecord[]>();
  /** Elements whose records cycled; each is warned about once. */
  private readonly cycled = new Set<ElementId>();

  constructor(private readonly now: () => number = () => performance.now()) {}

  /**
   * The events to send for a pass: one per `layout` element whose record
   * differs from the last one sent. Elements without a frame (inside a hidden
   * tab) are skipped, like `geometry`.
   */
  collect(result: LayoutResult): LayoutEvent[] {
    const events: LayoutEvent[] = [];
    const at = this.now();
    for (const [id, record] of result.layouts) {
      if (!result.frames.has(id)) continue;
      const event = layoutEventFor(id, record);
      const json = JSON.stringify(event);
      const last = this.reported.get(id);
      if (last && JSON.stringify(last) === json) continue;
      // Cycle guard: a record already sent within the window means the host
      // and Swift are trading answers (nested layouts measuring each other).
      // Stop re-asking until the window passes; Swift's latest answer stands.
      // The window keeps a legitimate repeat (data that changes back) from
      // being mistaken for a cycle and frozen for good.
      const recent = (this.history.get(id) ?? []).filter((r) => at - r.at < CYCLE_WINDOW_MS);
      if (recent.some((r) => r.json === json)) {
        this.history.set(id, recent);
        if (!this.cycled.has(id)) {
          this.cycled.add(id);
          console.warn(`[sb] layout ${id}: measurements cycle; keeping the last answer`);
        }
        continue;
      }
      recent.push({ json, at });
      this.history.set(id, recent);
      this.reported.set(id, event);
      events.push(event);
    }
    return events;
  }

  /** The element was removed: a re-created element with the same id reports afresh. */
  forget(id: ElementId): void {
    this.reported.delete(id);
    this.history.delete(id);
    this.cycled.delete(id);
  }

  clear(): void {
    this.reported.clear();
    this.history.clear();
    this.cycled.clear();
  }
}
